import {
  useDeleteCategoryMutation,
  useDeleteSubCategoryMutation,
  useGetDocumentCategoriesQuery,
  useLazyGetDocumentSubCategoriesQuery,
} from "../../../../../../store/apiSlice/fileApiSlice";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { GeneralModal } from "../../../../components/GeneralModal/GeneralModal";
import { DeleteModal } from "../../../../components/DeleteModal/DeleteModal";
import { CreateFileCategory } from "./CreateFileCategory";
import { SelectDropDown } from "../../Input/SelectDropDown";
import { CrudCategory } from "../../CrudCategory/CrudCategory";
import { useNotification } from "../../../../../../hooks/useNotification";

/**
 * FileCategories Component
 *
 * Handles management of document categories and sub-categories in a modal.
 * Supports:
 * - Viewing a list of categories or sub-categories
 * - Creating new categories / sub-categories
 * - Editing existing categories / sub-categories
 * - Deleting categories or sub-categories with confirmation modal
 *
 * Uses dropdown to select category when managing sub-categories.
 * Fetches data via RTK Query, dispatches updates, and shows notifications on actions.
 *
 * @component
 *
 * @param {Function} closeCategoryModal - Closes the category/sub-category modal.
 * @param {"category"|"subCategory"|null} isOpenCategoryModal - Determines if showing category or sub-category management.
 *
 */

export const FileCategories = ({ closeCategoryModal, isOpenCategoryModal }) => {
  const [fileCategoryName, setFileCategoryName] = useState({ name: "", id: null });
  const [categoryId, setCategoryId] = useState(null);
  const [isOpenCreateFileCategory, setisOpenCreateFileCategory] = useState(false);
  const [deleteSubCategory] = useDeleteSubCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();
  const [getSubDocuments] = useLazyGetDocumentSubCategoriesQuery();
  const categoryType = useRef(null);
  const notify = useNotification();

  useGetDocumentCategoriesQuery();
  const { documentCategoriesSelection, documentSubCategoriesSelection } = useSelector(
    state => state.file,
  );
  const [selectDocuments, setSelectDocuments] = useState([]);

  useEffect(() => {
    let docx = [];
    isOpenCategoryModal === "category"
      ? (docx = documentCategoriesSelection)
      : (docx = documentSubCategoriesSelection);
    docx = [...docx];
    docx.sort((a, b) => b.id - a.id);

    if (!categoryId && isOpenCategoryModal === "subCategory") {
      setSelectDocuments([]);
    } else {
      setSelectDocuments(
        docx.filter(
          item =>
            item?.document_category?.type?.id === "custom" ||
            item?.type?.id === "custom" ||
            item?.is_default === false,
        ),
      );
    }

    if (isOpenCategoryModal) {
      categoryType.current = isOpenCategoryModal;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documentCategoriesSelection,
    documentSubCategoriesSelection,
    isOpenCategoryModal,
    categoryId,
  ]);

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState(null);

  const closeCreateFileCategory = () => {
    setisOpenCreateFileCategory(false);
  };

  const handleFileCategoryModal = () => {
    setisOpenCreateFileCategory(true);
    setFileCategoryName({ name: "", id: null });
  };

  const handleEdit = val => {
    setFileCategoryName({ name: val.name, id: val.id });
    setisOpenCreateFileCategory(true);
  };

  const closeDeleteModal = () => setIsOpenDeleteModal(false);
  const openDeleteModal = (id, name) => {
    setIsOpenDeleteModal(true);
    setDeleteData({ id, name });
  };

  const handleDeleteModal = async () => {
    const notifyData = { type: "success", message: "" };
    try {
      if (categoryType.current === "category") {
        await deleteCategory({ id: deleteData.id }).unwrap();
        notifyData.message = "Deletion complete—category removed.";
      } else if (categoryType.current === "subCategory") {
        await deleteSubCategory({ id: deleteData.id }).unwrap();
        getSubDocuments(new URLSearchParams({ document_category_id: categoryId }).toString());
        notifyData.message = "Deletion complete—sub category removed.";
      }
    } catch (error) {
      notifyData.type = "error";
      notifyData.message = error?.data?.detail ?? "Something went wrong";
    }
    notify(notifyData);
    closeDeleteModal();
  };
  const handleDropDown = id => {
    setCategoryId(id);
    getSubDocuments(new URLSearchParams({ document_category_id: id }).toString());
  };
  const findCat = (id, items = []) => {
    const nameOfVal = items.find(item => item.id === id)?.name;
    return nameOfVal;
  };

  const categoryFilterByType = useMemo(() => {
    const sortedCat = [...documentCategoriesSelection];
    sortedCat.sort((a, b) => a.id - b.id);
    return sortedCat;
  }, [documentCategoriesSelection]);

  const isCategory = isOpenCategoryModal === "category";
  const needsParentCategory = !isCategory && !categoryId;

  return (
    <CategoryOverlay
      onMouseDown={event => {
        if (event.target === event.currentTarget) closeCategoryModal();
      }}
    >
      {isOpenDeleteModal && (
        <DeleteModal
          handleDeleteModal={handleDeleteModal}
          closeDeleteModal={closeDeleteModal}
          nameOfView={`Think carefully! Deleting ${isCategory ? "category" : "sub category"} - "${deleteData.name}"  is permanent, and the data will be lost forever.`}
          labelForConfirmCheck={`I understand that the data cannot be retrieved after deleting.`}
        />
      )}
      {isOpenCreateFileCategory ? (
        <CreateFileCategory
          placeholderForInput={isCategory ? "Category name" : "Subcategory name"}
          headerOfCreateName={isCategory ? "Create Category" : "Create Sub Category"}
          fileCategoryName={fileCategoryName}
          closeCreateFileCategory={closeCreateFileCategory}
          isOpenCategoryModal={isOpenCategoryModal}
          getSubCategory={getSubDocuments}
          categoryId={categoryId}
        />
      ) : (
        <GeneralModal
          titleOfModal={isCategory ? "Categories" : "Sub Categories"}
          widthOfGeneralModal="420px"
          classNameOfModal="fileCategoryModal"
          closeModal={closeCategoryModal}
        >
          {!isCategory && (
            <div className="fileManagerSelect">
              <SelectDropDown
                widthOfSelectElement="100%"
                heightOfSelectElement="36px"
                labelForInput="Category"
                placeholderForInput="Select a category"
                options={categoryFilterByType}
                handleSelect={handleDropDown}
                nameOfInput="category"
                selectedVal={findCat(categoryId, documentCategoriesSelection)}
                isOptionWithId={true}
              />
            </div>
          )}
          {needsParentCategory && (
            <p className="hint">Select a category to see and add its subcategories.</p>
          )}
          <CrudCategory
            variant="panel"
            handleFileCategoryModal={handleFileCategoryModal}
            btnNameForAddCat={isCategory ? "Add category" : "Add subcategory"}
            handleEdit={handleEdit}
            openDeleteModal={openDeleteModal}
            listOfCat={selectDocuments}
            isBtnDisabled={needsParentCategory}
            emptyLabel={
              isCategory ? "No custom categories yet." : "No custom subcategories yet."
            }
          />
        </GeneralModal>
      )}
    </CategoryOverlay>
  );
};

const CategoryOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 36, 77, 0.28);

  .fileCategoryModal {
    border-radius: 8px;
    box-shadow: 0 18px 48px rgba(16, 36, 77, 0.18);
  }

  .fileCategoryModal > .generalContent {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }

  .fileCategoryModal > h1 {
    border-radius: 8px 8px 0 0;
  }

  .fileCategoryModal > .createMilestone:not(:has(button)) {
    display: none;
  }

  .hint {
    margin: 0;
    color: #676879;
    font-size: 13px;
    line-height: 20px;
  }

  .fileManagerSelect .selectDropDown,
  .fileManagerSelect .selectElement {
    width: 100%;
  }

  .fileManagerSelect .labelForInput {
    margin-bottom: 4px;
    color: #4a4a68;
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
  }

  .fileManagerSelect .inputValuePlace {
    height: 36px;
    border-radius: 6px;
    border-color: #d5deea;
    background-color: #fff;
    color: #8b93a7;
    padding-right: 28px;
  }

  .fileManagerSelect .inputValuePlace.hasValue {
    color: #10244d;
  }

  .fileManagerSelect .inputValuePlace.focusInput {
    border-color: #2060ea;
    box-shadow: 0 0 0 3px rgba(32, 96, 234, 0.12);
  }

  .fileManagerSelect .optionsOfSelect {
    top: calc(100% + 4px);
    left: 0;
    z-index: 30;
    padding: 6px;
    border: 1px solid #e5eaf0;
    border-radius: 8px;
    box-shadow: 0 10px 28px rgba(16, 36, 77, 0.12);
  }

  .fileManagerSelect .optionItem {
    border-radius: 6px;
    padding: 8px 10px;
  }

  .fileCategoryModal .labelForText {
    gap: 6px;
  }

  .fileCategoryModal .labelForText > span {
    color: #4a4a68;
    font-size: 12px;
    font-weight: 600;
  }

  .fileCategoryModal .labelForText .inputBox {
    height: 36px;
    padding: 8px 12px;
    border-radius: 6px;
    border-color: #d5deea;
    color: #10244d;
  }

  .fileCategoryModal .createMilestone {
    padding: 4px 16px 16px;
  }

  .fileCategoryModal .createMilestone button {
    height: 32px;
    border-radius: 6px;
    font-weight: 600;
  }
`;
